import React, { FC, useEffect, useState } from 'react'
import styles from 'styles/components/list.module.scss'
import print from 'styles/components/print.module.scss'
import text from 'styles/utility/text.module.scss'
import format from 'date-fns/format'
import HTMLReactParser from 'html-react-parser'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { MainHeadline } from 'components/Typography'
import { getBrowserName } from 'lib/judge'
import { fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { getOrders } from 'reducks/checkout/selectors'
import { OrderBreadListItem } from 'components/List'
import { CHROME_SHEET_HEIGHT, ORDER_HEADER_FOOTER, SAFARI_SHEET_HEIGHT } from 'constants/print'
import { useFlatCheckoutItems } from 'hooks/checkout'
import { useOrdersByBread } from 'hooks/order'
import { fetchBreads } from 'reducks/bread/operations'
import { findBreadShortName } from 'lib/helper'
import { getBreads } from 'reducks/bread/selectors'

type Props = {
  date: string | undefined
  title: string
}

const PrintOrderByBreadTemplate: FC<Props> = (props) => {
  const { date, title } = props

  const dispatch = useDispatch()
  const router = useSelector((state: State) => state.router)
  const expandedBreadIds = router.location.query.expandedBreadIds?.split(',') || []
  const breads = useSelector((state: State) => getBreads(state))
  const orders = useSelector((state: State) => getOrders(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const items = useFlatCheckoutItems(orders)
  const ordersByBread = useOrdersByBread(items)
  const [printedItems, setPrintedItems] = useState<JSX.Element[]>([])
  const [isReadyPrint, setReadyPrint] = useState<boolean>(false)
  const isChrome = getBrowserName() === 'chrome'

  // 印刷用レイアウトの作成
  useEffect(() => {
    if (ordersByBread.length === 0 || breads.length === 0) return
    setTimeout(() => {
      const documents = Array.from(document.getElementsByClassName('listItem-parent'))
      if (documents.length === 0) return
      const items: JSX.Element[] = []
      const SHEET_HEIGHT = isChrome
        ? CHROME_SHEET_HEIGHT - ORDER_HEADER_FOOTER
        : SAFARI_SHEET_HEIGHT - ORDER_HEADER_FOOTER
      let currentHeight = 0
      // 改ページが必要なListItemのindexを取得
      const breakBeforePages: number[] = [0]
      for (let i = 0; i < documents.length; i = (i + 1) | 0) {
        const document = documents[i]
        if (!document) continue
        currentHeight += document.clientHeight
        if (currentHeight >= SHEET_HEIGHT) {
          breakBeforePages.push(i)
          currentHeight = document.clientHeight
        }
      }
      for (let i = 0; i < breakBeforePages.length; i = (i + 1) | 0) {
        const startRefIndex = breakBeforePages[i]
        // 最終ページかどうかを判定
        const slicedDocuments =
          i + 1 === breakBeforePages.length
            ? documents.slice(startRefIndex)
            : documents.slice(startRefIndex, breakBeforePages[i + 1])

        items.push(
          <section className={isChrome ? print.sheet_chrome : print.sheet_safari}>
            {isChrome ? (
              <Spacer size={49} /> /* Chrome用ヘッダの余白 */
            ) : (
              <>
                <Spacer size={12} />
                <FlexRow justifyContent={'between'}>
                  <MainHeadline headline={title} />
                  <p className={`${text.font_small} ${text.align_right}`}>
                    {format(new Date(), 'yyyy年M月d日HH:mm時点')}
                  </p>
                </FlexRow>
                <Spacer size={12} />
              </>
            )}
            <div className={styles.order} style={{ border: `1px solid #BABABA`, borderRadius: '8px' }}>
              <div className={styles.order__header} style={{ background: '#fff' }}>
                <span className={styles.order__header__cell} style={{ color: '#222' }}>
                  商品名
                </span>
                <span className={styles.order__header__cell} style={{ color: '#222' }}>
                  個数(個)
                </span>
              </div>
              {slicedDocuments.map((document) => {
                if (!document) return
                return HTMLReactParser(document.innerHTML)
              })}
            </div>
            <div className={print.footer}>
              ページ {i + 1}/{breakBeforePages.length}
            </div>
          </section>
        )
      }
      setPrintedItems([...items])
      setReadyPrint(true)
    }, 1000)
  }, [ordersByBread, breads])

  useEffect(() => {
    if (!isReadyPrint) return
    setTimeout(() => {
      if (isChrome) {
        window.print()
      } else {
        document.execCommand('print', false, undefined)
      }
    }, 500)
  }, [isReadyPrint])

  // 日付が変わったら受取番号と注文を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initOrders())
    dispatch(fetchOrders(date, 'すべて', ['daily', 'preorder'], ['succeeded', 'requires_capture'], storeRef))
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [date, storeRef])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'name'))
  }, [storeRef])

  return (
    <div>
      <section className={print.screenArea}>
        <Spacer size={12} />
        <FlexRow justifyContent={'between'}>
          <MainHeadline headline={title} />
          <p className={`${text.font_small} ${text.align_right}`}>{format(new Date(), 'yyyy年M月d日HH:mm時点')}</p>
        </FlexRow>
        <Spacer size={12} />
        <div className={styles.order}>
          <div className={styles.order__header}>
            <span className={styles.order__header__cell}>商品名</span>
            <span className={styles.order__header__cell}>個数(個)</span>
          </div>
          {ordersByBread
            .sort((a, b) => {
              const aBread = breads.find((bread) => bread.bread_ref?.id === a.breadRef.id)
              const bBread = breads.find((bread) => bread.bread_ref?.id === b.breadRef.id)
              // bread の order の昇順で並び替える
              return (aBread?.order || 9999) - (bBread?.order || 9999)
            })
            .map((orderByBread, index) => {
              const pickupableTimes = orderByBread.pickupable_times.sort((a, b) => {
                return a.pickupable_time < b.pickupable_time ? -1 : 1
              })
              const breadName = findBreadShortName(breads, orderByBread.breadName, orderByBread.breadRef)
              const isExpandedPrint = expandedBreadIds.includes(orderByBread.breadRef.id)
              return (
                <div className={'listItem-parent'} key={`${orderByBread.breadRef.id}-${index}`}>
                  <OrderBreadListItem
                    breadRef={orderByBread.breadRef}
                    breadName={breadName}
                    isExpandedPrint={isExpandedPrint}
                    isPrintPage={true}
                    manufacturerName={orderByBread.manufacturerName}
                    pickupableTimes={pickupableTimes}
                    storeName={orderByBread.storeName}
                  />
                </div>
              )
            })}
        </div>
      </section>
      {/* 印刷用エリア(ブラウザでは非表示) */}
      <section className={print.printArea}>
        {isChrome && (
          <div className={print.header}>
            <MainHeadline headline={title} />
            <p className={`${text.font_small} ${text.align_right}`}>{format(new Date(), 'yyyy年M月d日HH:mm時点')}</p>
          </div>
        )}
        {printedItems}
      </section>
    </div>
  )
}

export default PrintOrderByBreadTemplate
